import type { SectionsComparison } from '../../../features/engagement/domain/models/sections'
import { formatDurationHours, getSegmentColors } from './section-display'

export interface SegmentCardVm {
  key: string
  label: string
  accentColor: string
  /** First paid tier gets the accent-border highlight */
  highlighted: boolean
  time: string
  visitsPerUser: string
  interactionsPerUser: string
}

// Per-user counts are averages; rendered as whole numbers with thousands separators
const formatPerUserCount = (value: number): string => {
  const safeValue = Number.isFinite(value) && value > 0 ? Math.round(value) : 0
  return safeValue.toLocaleString('en-US')
}

export const getSegmentCards = (comparison: SectionsComparison): SegmentCardVm[] => {
  const colors = getSegmentColors(comparison.segments)
  const firstPaidKey = comparison.segments.find((segment) => segment.kind === 'paid')?.key

  return comparison.segments.map((segment) => ({
    key: segment.key,
    label: segment.label,
    accentColor: colors[segment.key],
    highlighted: segment.key === firstPaidKey,
    time: formatDurationHours(segment.totals.secondsPerUser),
    visitsPerUser: formatPerUserCount(segment.totals.visitsPerUser),
    interactionsPerUser: formatPerUserCount(segment.totals.interactionsPerUser)
  }))
}

export interface PaidVsFreeHeadline {
  tier: string
  multiplier: string
}

/**
 * "A <top paid tier> user spends <X.X>× more time than a free user".
 * Hidden (null) when there is no paid or free segment, the free segment has no
 * web users, either seconds-per-user is zero, or the ratio is not above 1
 * (the "more time" claim would be false or meaningless).
 */
export const getPaidVsFreeHeadline = (comparison: SectionsComparison): PaidVsFreeHeadline | null => {
  const topPaid = comparison.segments.find((segment) => segment.kind === 'paid')
  const free = comparison.segments.find((segment) => segment.kind === 'free')

  if (!topPaid || !free || free.webUsers <= 0) return null

  const paidSeconds = topPaid.totals.secondsPerUser
  const freeSeconds = free.totals.secondsPerUser
  if (!(paidSeconds > 0) || !(freeSeconds > 0)) return null

  const ratio = paidSeconds / freeSeconds
  if (!Number.isFinite(ratio) || ratio <= 1) return null

  return { tier: topPaid.label, multiplier: ratio.toFixed(1) }
}
